import React from 'react';
import { OptimizationResult } from '../types';
import { ResultsDisplay } from './ResultsDisplay';
import { downloadPalletsCSV } from '../utils/exportCsv';

interface PostOptimizationPageProps {
  result: OptimizationResult;
  onNewOptimization: () => void;
  onViewVisualization: () => void;
}

export const PostOptimizationPage: React.FC<PostOptimizationPageProps> = ({
  result,
  onNewOptimization,
  onViewVisualization
}) => {
  return (
    <div>
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Optimization Complete!</h1>
        <p className="text-gray-600 text-lg">
          Your pallets have been optimized with {result.efficiency.toFixed(1)}% efficiency
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8 justify-center">
        <button
          onClick={onViewVisualization}
          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-8 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium text-lg flex items-center justify-center space-x-3 shadow-lg"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span>View 2D Visualization</span>
        </button>
        <button
          onClick={() => downloadPalletsCSV(result)}
          className="bg-green-600 text-white py-4 px-8 rounded-lg hover:bg-green-700 transition-all duration-200 font-medium text-lg flex items-center justify-center space-x-3 shadow-lg"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
          </svg>
          <span>Export CSV</span>
        </button>
        <button
          onClick={onNewOptimization}
          className="bg-gray-600 text-white py-4 px-8 rounded-lg hover:bg-gray-700 transition-all duration-200 font-medium text-lg flex items-center justify-center space-x-3 shadow-lg"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>New Optimization</span>
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="text-3xl font-bold text-blue-600">{result.totalPallets}</div>
          <div className="text-sm text-gray-600">Total Pallets</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="text-3xl font-bold text-green-600">{result.efficiency.toFixed(1)}%</div>
          <div className="text-sm text-gray-600">Efficiency</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="text-3xl font-bold text-purple-600">
            {result.pallets.reduce((sum, pallet) => 
              sum + pallet.layers.reduce((layerSum, layer) => layerSum + layer.boxes.length, 0), 0
            )}
          </div>
          <div className="text-sm text-gray-600">Total Boxes</div>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 text-center">
          <div className="text-3xl font-bold text-orange-600">
            {result.pallets.reduce((sum, pallet) => sum + pallet.layers.length, 0)}
          </div>
          <div className="text-sm text-gray-600">Total Layers</div>
        </div>
      </div>

      {/* Pallet Dimensions Info */}
      <div className="bg-blue-50 rounded-xl p-6 mb-8">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">Pallet Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{result.palletDimensions.length}</div>
            <div className="text-sm text-blue-700">Length (cm)</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{result.palletDimensions.width}</div>
            <div className="text-sm text-blue-700">Width (cm)</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{result.palletDimensions.maxHeight}</div>
            <div className="text-sm text-blue-700">Max Height (cm)</div>
          </div>
        </div>
      </div>

      {/* Detailed Results */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <ResultsDisplay result={result} />
      </div>

      {/* Additional Actions */}
      <div className="mt-8 bg-gray-50 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">What's Next?</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Visualize</h4>
            <p className="text-sm text-gray-600">View 2D visualization of your optimized pallets</p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Export</h4>
            <p className="text-sm text-gray-600">Download results for your shipping documentation</p>
          </div>
          
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">Optimize Again</h4>
            <p className="text-sm text-gray-600">Start a new optimization with different parameters</p>
          </div>
        </div>
      </div>
    </div>
  );
}; 